import {
  useMemo,
  useState,
  useEffect,
  useEffectEvent,
  useRef,
  Fragment,
} from "react";
import { useNavigate } from "react-router";
import dayjs from "dayjs";
import {
  DISCOVER_SEARCH,
  UNIFIED_CHECKOUT_BASKET_DETAILS,
  UNIFIED_CHECKOUT_BASKET_PAY,
  UNIFIED_CHECKOUT_BASKET_SUCCESS,
} from "../../constants/routes";
import {
  useUnifiedCheckout,
  SupplierStorefrontInfo,
} from "./UnifiedCheckoutProvider.shared";
import { CheckoutPageLayout } from "../stockList/checkoutV2/lib/CheckoutPageLayout";
import { CheckoutPageLoading } from "../stockList/checkoutV2/lib/CheckoutPageLoading";
import { CheckoutOrderSummary } from "../stockList/checkoutV2/lib/CheckoutOrderSummary";
import { mapBasketOrderToCheckoutOrderItems } from "../stockList/checkoutV2/lib/mapBasketOrderToCheckoutOrderItems";
import { isPromotionalOrderItem } from "../stockList/categoryPage/Storefront/StorefrontPromotion/isPromotionalOrderItem";
import { useCheckoutPaymentOptions } from "../../hooks/api/v2/checkout/useCheckoutPaymentOptions";
import { UnifiedCheckoutPaymentLinkSettingsDialog } from "./components/UnifiedCheckoutPaymentLinkSettingsDialog";
import {
  CheckoutInvoiceMethod,
  CheckoutPaymentType,
} from "../stockList/checkoutV2/lib/types";
import { useOrderCreate } from "../../hooks/api/v2/order/v2/useOrderCreate";
import { useSellarCurrentBusiness } from "../../hooks/useSellarCurrentBusiness.shared";
import { useBusinessUpdate } from "../../hooks/api/v2/business/useBusinessUpdate";
import { usePaymentMethodList } from "../../hooks/api/v2/paymentMethod/usePaymentMethodList";
import { useLegalEntityCompanyCreditInfo } from "../../hooks/api/v2/legalEntity/useLegalEntityCompanyCreditInfo";
import type { Stripe, StripeElements } from "@stripe/stripe-js";
import { useFirebaseUser } from "../../hooks/useFirebaseUser.shared";
import {
  mapPaymentLinkRequested,
  mapToLegacyPaymentType,
} from "../stockList/checkoutV2/lib/mapToLegacyPaymentType";
import { useMarketStorefrontSessionId } from "../../hooks/useMarketStorefrontSessionTracking";
import { useUnifiedCheckoutTracking } from "./useUnifiedCheckoutTracking";
import { ContextOrderBasketType } from "../../hooks";
import {
  type PlaceOrderFailureStage,
  usePlaceOrder,
} from "../stockList/checkoutV2/lib/usePlaceOrder";
import { useValidateCheckout } from "../stockList/checkoutV2/lib/useValidateCheckout";
import { isHttpError } from "~/utils/httpClient";
import {
  BusinessGetResponseType,
  CheckoutPaymentStatusResponseType,
  SellarDistributionTimePreferenceType,
} from "@sellar-io/api-v2/types";

import { AlwaysStripeProvider } from "./components/AlwaysStripeProvider";
import { SupplierPaymentCard } from "./components/SupplierPaymentCard";
import { PayButton } from "./components/PayButton";
import { Button } from "@sellar-io/components";
import { UnifiedCheckoutOrderPaymentStatus } from "./components/UnifiedCheckoutOrderPaymentStatus";
import { useUnifiedCheckoutSummaryItems } from "./hooks/useUnifiedCheckoutSummaryItems";
import { useQueryClient } from "@tanstack/react-query";
import { ServerStateKeys } from "../../hooks/api/v2/ServerStateKeys";
import {
  apiV2AssertResponse,
  apiV2Request,
} from "../../hooks/api/v2/apiV2Request";
import { useUnifiedCheckoutPaymentReturn } from "./hooks/useUnifiedCheckoutPaymentReturn";
import { useUnifiedCheckoutCardPayment } from "./hooks/useUnifiedCheckoutCardPayment";
import { UnifiedCheckoutDeliveryDateSummary } from "./components/UnifiedCheckoutDeliveryDateSummary";
import { UnifiedCheckoutPayAlerts } from "./components/UnifiedCheckoutPayAlerts";
import {
  type UnifiedCheckoutPriceChange,
  UnifiedCheckoutPriceChangeDialog,
} from "./components/UnifiedCheckoutPriceChangeDialog";
import {
  useUnifiedCheckoutBasketList,
  useUnifiedCheckoutBasketUpdateItem,
} from "../../hooks/api/v2/unified-checkout-basket/useUnifiedCheckoutBaskets";
import { calculateUnifiedCheckoutOrderPricing } from "./lib/calculateUnifiedCheckoutOrderPricing";

const EMPTY_NUMBER_ARRAY: number[] = [];
const INSUFFICIENT_SELLAR_CREDIT_MESSAGE =
  "You do not have enough Sellar Credit remaining to cover this order. Please choose another payment method to continue.";
const PAYMENT_STATUS_POLL_INTERVAL_MS = 1000;
const PAYMENT_STATUS_POLL_ATTEMPTS = 5;

// ─── Helpers ─────────────────────────────────────────────────────────────────

const mapTimeSlotPreference = (
  preference: "AM" | "PM" | "flexible" | null | undefined,
): SellarDistributionTimePreferenceType | null => {
  switch (preference) {
    case "AM":
      return "am";
    case "PM":
      return "pm";
    case "flexible":
      return "allDay";
    default:
      return null;
  }
};

type ReconciliationItem = {
  ProductId: number;
  PromotionRewardItemId?: number | null;
  quantity: number;
};

const basketItemsChanged = (
  currentItems: ReconciliationItem[],
  refreshedItems: ReconciliationItem[],
) => {
  const getQuantitiesByIdentity = (items: ReconciliationItem[]) => {
    const quantities = new Map<string, number>();
    items.forEach((item) => {
      const identity = `${item.ProductId}:${item.PromotionRewardItemId ?? "standard"}`;
      quantities.set(identity, (quantities.get(identity) ?? 0) + item.quantity);
    });
    return quantities;
  };

  const currentQuantities = getQuantitiesByIdentity(currentItems);
  const refreshedQuantities = getQuantitiesByIdentity(refreshedItems);

  return (
    currentQuantities.size !== refreshedQuantities.size ||
    Array.from(currentQuantities).some(
      ([identity, quantity]) => refreshedQuantities.get(identity) !== quantity,
    )
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type SelectedPaymentMethod = {
  type: CheckoutPaymentType | "";
  paymentMethodId?: string;
  brand?: string;
  last4?: string;
  paymentLinkRequested?: boolean;
  invoiceMethod?: CheckoutInvoiceMethod;
};

type SupplierGroup = {
  supplierId: number;
  orders: ContextOrderBasketType[];
  info: SupplierStorefrontInfo | undefined;
  subTotal: number;
  discount: number;
  vat: number;
  total: number;
  currency: "gbp" | "sgd";
  itemCount: number;
};

type PendingPriceChange = UnifiedCheckoutPriceChange & {
  stripe: Stripe | null;
  stripeElements: StripeElements | null;
};

// ─── Main Component ──────────────────────────────────────────────────────────

export const UnifiedCheckoutPayPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { sdBasket, sdOrders, supplierInfoMap, isLoading } =
    useUnifiedCheckout();
  const { error: paymentReturnError, isValidatingPaymentReturn } =
    useUnifiedCheckoutPaymentReturn(sdBasket.uuid);
  const [paymentMethods, setPaymentMethods] = useState<Record<number, string>>(
    {},
  );
  const [paymentLinkRequests, setPaymentLinkRequests] = useState<
    Record<number, boolean>
  >({});
  const [invoiceMethods, setInvoiceMethods] = useState<
    Record<number, CheckoutInvoiceMethod>
  >({});
  const { business } = useSellarCurrentBusiness();
  const businessUpdateMutation = useBusinessUpdate(business?.id);
  const updateBasketItem = useUnifiedCheckoutBasketUpdateItem();
  const basketListQuery = useUnifiedCheckoutBasketList();
  const [reconciliationError, setReconciliationError] = useState(false);
  const [reconciliationFetchError, setReconciliationFetchError] =
    useState(false);
  const deliveryInstructionsUpdateStartedRef = useRef(false);
  const retailerId = business?.Retailer?.id ?? 0;
  const sellarCreditBalanceQuery = useLegalEntityCompanyCreditInfo(
    {
      RetailerId: retailerId,
    },
    {
      enabled: !!retailerId,
    },
  );
  const sellarCreditBalance =
    sellarCreditBalanceQuery.data?.creditAmountAvailable ?? 0;
  const canUserViewSellarCreditBalance =
    sellarCreditBalanceQuery.data?.userCanViewSellarCreditBalance ?? false;
  const sellarCreditStatus =
    sellarCreditBalanceQuery.data?.status ?? "creditCheckNotPerformed";
  const isCompanyVerified =
    sellarCreditBalanceQuery.data?.isCompanyVerified ?? null;
  const creditProvider = sellarCreditBalanceQuery.data?.creditProvider ?? "two";
  // Keep the supplier and its Stripe Elements mounted until Stripe has
  // completed confirmation (including any SCA challenge).
  const orderCreateMutation = useOrderCreate();
  const cardOrderCreateMutation = useOrderCreate({
    invalidateUnifiedCheckoutBasket: false,
  });
  const { user: firebaseUser } = useFirebaseUser();
  const refreshUnifiedCheckoutBasket = () =>
    queryClient.invalidateQueries({
      queryKey: [
        firebaseUser?.uid,
        business?.id,
        ServerStateKeys.UnifiedCheckoutBaskets,
      ],
    });
  const refreshBasketWhenPaymentSettles = async (orderId: number) => {
    try {
      for (let attempt = 0; attempt < PAYMENT_STATUS_POLL_ATTEMPTS; attempt++) {
        const paymentStatus = await apiV2Request({
          user: firebaseUser,
          business,
          method: "get",
          path: `checkout/payment-status/${orderId}`,
        }).then(
          (response) =>
            apiV2AssertResponse<CheckoutPaymentStatusResponseType>(response)
              .data,
        );

        if (
          paymentStatus.status === "failed" ||
          paymentStatus.status === "succeeded"
        ) {
          break;
        }

        await new Promise((resolve) =>
          setTimeout(resolve, PAYMENT_STATUS_POLL_INTERVAL_MS),
        );
      }
    } finally {
      await refreshUnifiedCheckoutBasket();
    }
  };
  const { sessionId, journeySource } = useMarketStorefrontSessionId();
  const { trackResult, trackStepViewed } = useUnifiedCheckoutTracking();
  const trackPayViewed = useEffectEvent(() => trackStepViewed("pay"));
  useEffect(() => {
    trackPayViewed();
  }, []);
  const [failedPaymentOrderId, setFailedPaymentOrderId] = useState<number>();
  const handlePlaceOrderError = (
    failureStage: PlaceOrderFailureStage,
    error: unknown,
    orderId?: number,
  ) => {
    setFailedPaymentOrderId(failureStage === "payment" ? orderId : undefined);
    if (failureStage === "payment" && orderId) {
      refreshBasketWhenPaymentSettles(orderId).catch(() => undefined);
    }
    trackResult({
      result:
        (sdBasket.paidSupplierIds?.length ?? 0) > 0 ? "partial" : "failed",
      failureStage,
      errorCategory:
        failureStage === "order-creation"
          ? isHttpError(error)
            ? "order-request-failed"
            : "unexpected-order-error"
          : "payment-attempt-failed",
    });
  };
  const {
    loading: isPlacingNonCardOrder,
    error: nonCardPlaceOrderError,
    placeOrder,
    setError: setNonCardPlaceOrderError,
  } = usePlaceOrder({ onError: handlePlaceOrderError });
  const {
    error: cardPlaceOrderError,
    isPending: isPlacingCardOrder,
    payWithCard,
    setError: setCardPlaceOrderError,
  } = useUnifiedCheckoutCardPayment({
    onError: handlePlaceOrderError,
    onSettled: refreshUnifiedCheckoutBasket,
  });
  const isPlacingOrder = isPlacingNonCardOrder || isPlacingCardOrder;
  const placeOrderError = cardPlaceOrderError || nonCardPlaceOrderError;
  const { handleValidateCheckout, outOfStockProducts } = useValidateCheckout();

  const [dialogSupplierId, setDialogSupplierId] = useState<number | null>(null);
  const [validationErrorSupplierId, setValidationErrorSupplierId] = useState<
    number | null
  >(null);
  const [inactiveProducts, setInactiveProducts] = useState<
    Array<{ id: number; name: string }>
  >([]);
  const [pendingPriceChange, setPendingPriceChange] =
    useState<PendingPriceChange | null>(null);

  // Group orders by supplier
  const supplierGroups: SupplierGroup[] = useMemo(() => {
    const groups = new Map<number, ContextOrderBasketType[]>();
    sdOrders.forEach((order) => {
      const supplierId = order.SupplierId;
      if (supplierId == null) return;
      const list = groups.get(supplierId) || [];
      list.push(order);
      groups.set(supplierId, list);
    });

    return Array.from(groups.entries())
      .sort(
        ([firstSupplierId], [secondSupplierId]) =>
          firstSupplierId - secondSupplierId,
      )
      .map(([supplierId, orders]) => {
        const info = supplierInfoMap.get(supplierId);
        const allItems = orders.flatMap((order) => order.OrderItems ?? []);
        const storefront = info?.storefront;
        const vatPercentage = orders[0]?.vatPercentage ?? 0;
        const {
          subtotal: subTotal,
          discount,
          vat,
          total,
        } = calculateUnifiedCheckoutOrderPricing({
          orderItems: allItems,
          storefront,
          vatPercentage,
        });
        const currency = orders[0]?.currency ?? "gbp";
        return {
          supplierId,
          orders,
          info,
          subTotal,
          discount,
          vat,
          total,
          currency,
          itemCount: orders.reduce(
            (acc, o) =>
              acc +
              (o.OrderItems ?? []).reduce(
                (count, item) => count + item.quantity,
                0,
              ),
            0,
          ),
        };
      });
  }, [sdOrders, supplierInfoMap]);

  const paidSupplierIds = sdBasket.paidSupplierIds || EMPTY_NUMBER_ARRAY;
  const {
    createdOrders,
    orderItemList: allCheckoutItems,
    orderSummary: totalOrderSummary,
  } = useUnifiedCheckoutSummaryItems();

  // Draft orders disappear from the basket as they are placed. Retain the
  // first-seen supplier sequence so completed cards replace drafts in place
  // instead of jumping to a separate section of the panel.
  const [supplierDisplayOrder] = useState(() =>
    [
      ...new Set([
        ...supplierGroups.map((group) => group.supplierId),
        ...paidSupplierIds,
        ...(sdBasket.pendingSupplierIds ?? []),
        ...(sdBasket.failedSupplierIds ?? []),
      ]),
    ].sort((first, second) => first - second),
  );
  const displayedSupplierIds = useMemo(() => {
    const visibleSupplierIds = [
      ...supplierGroups.map((group) => group.supplierId),
      ...createdOrders.map((order) => order.SupplierId),
    ];
    const knownSupplierIds = new Set(supplierDisplayOrder);
    return [
      ...new Set([
        ...supplierDisplayOrder,
        ...visibleSupplierIds.filter(
          (supplierId) => !knownSupplierIds.has(supplierId),
        ),
      ]),
    ];
  }, [createdOrders, supplierDisplayOrder, supplierGroups]);

  const unpaidSuppliers = useMemo(
    () =>
      supplierGroups.filter(
        (group) => !paidSupplierIds.includes(group.supplierId),
      ),
    [paidSupplierIds, supplierGroups],
  );

  const [activeSupplierId, setActiveSupplierId] = useState<number | null>(null);

  // Initialise the active supplier to the first unpaid one, and move on when
  // the current supplier's order is created, even if payment fails.
  if (activeSupplierId == null && unpaidSuppliers[0]) {
    setActiveSupplierId(unpaidSuppliers[0].supplierId);
  } else if (
    activeSupplierId != null &&
    !unpaidSuppliers.some((group) => group.supplierId === activeSupplierId)
  ) {
    const nextUnpaid = unpaidSuppliers.find(
      (group) => group.supplierId !== activeSupplierId,
    );
    setActiveSupplierId(nextUnpaid?.supplierId ?? null);
  }

  // Fetch saved payment methods for the active / dialog supplier
  const paymentMethodsTargetId = dialogSupplierId || activeSupplierId;
  const paymentMethodsQuery = usePaymentMethodList({
    query:
      paymentMethodsTargetId != null
        ? [
            {
              value: paymentMethodsTargetId,
              type: "SupplierId" as const,
              modifier: "EQUALS" as const,
            },
          ]
        : [],
  });

  const savedPaymentMethods = useMemo(() => {
    return (
      paymentMethodsQuery.data?.pages[0]
        ?.map((pm) => ({
          stripePaymentMethodId: pm.Suppliers[0]?.stripePaymentMethodId || "",
          brand: pm.brand || "unknown",
          last4Digits: pm.last4 || "",
        }))
        .filter((pm) => !!pm.stripePaymentMethodId) || []
    );
  }, [paymentMethodsQuery.data]);

  // Payment options for the active supplier (use the real order items and fulfillment type)
  const activeGroupPaymentOptions = useMemo(() => {
    if (activeSupplierId == null) return null;
    const group = supplierGroups.find((g) => g.supplierId === activeSupplierId);
    if (!group) return null;
    const order = group.orders[0];
    const orderItems = (order?.OrderItems ?? [])
      .filter((item) => !isPromotionalOrderItem(item))
      .map((item) => ({
        productId: item.ProductId,
        quantity: item.quantity,
      }));
    return {
      supplierId: group.supplierId,
      orderTotal: group.total,
      orderItems,
      orderFulfillmentType: "sellarDistribution" as const,
    };
  }, [activeSupplierId, supplierGroups]);

  const { data: availableOptions } = useCheckoutPaymentOptions(
    activeGroupPaymentOptions ?? {
      supplierId: 0,
      orderTotal: 0,
      orderItems: [],
      orderFulfillmentType: "sellarDistribution",
    },
    { enabled: !!activeGroupPaymentOptions },
  );

  // Prefer Sellar Credit when it is available, followed by supplier credit and
  // then the buyer's first saved card.
  if (
    activeSupplierId != null &&
    dialogSupplierId == null &&
    activeSupplierId === paymentMethodsTargetId &&
    paymentMethodsQuery.isSuccess &&
    !paymentMethods[activeSupplierId]
  ) {
    const defaultMethod = availableOptions?.includes("sellar-credit")
      ? "sellar-credit"
      : availableOptions?.includes("credit-account")
        ? "credit-account"
        : (savedPaymentMethods[0]?.stripePaymentMethodId ??
          (availableOptions?.includes("new-card-payment")
            ? "new-card"
            : undefined));
    if (defaultMethod) {
      setPaymentMethods((current) => ({
        ...current,
        [activeSupplierId]: defaultMethod,
      }));
    }
  }

  const getPaymentLinkRequested = (supplierId: number) =>
    paymentLinkRequests[supplierId] ?? false;

  const handlePay = async (
    supplierId: number,
    stripe: Stripe | null,
    stripeElements: StripeElements | null,
    selectionOverride?: string,
    acceptedPriceChange?: UnifiedCheckoutPriceChange,
  ) => {
    setCardPlaceOrderError("");
    setNonCardPlaceOrderError("");
    setValidationErrorSupplierId(null);
    setInactiveProducts([]);
    setReconciliationError(false);
    setReconciliationFetchError(false);
    setPendingPriceChange(null);

    let group = supplierGroups.find((g) => g.supplierId === supplierId);
    if (!group) return;

    let order = group.orders[0];
    if (!order) return;

    const refreshedBaskets = await basketListQuery.refetch();
    if (refreshedBaskets.isError || !refreshedBaskets.data) {
      setReconciliationFetchError(true);
      return;
    }
    const refreshedBasket = refreshedBaskets.data?.find(
      (basket) => basket.uuid === sdBasket.uuid,
    );
    const refreshedOrder = refreshedBasket?.DraftOrders.find(
      (draft) => draft.SupplierId === supplierId,
    );
    const deliveryDateChanged =
      Boolean(refreshedOrder?.expectedDeliveryAt) !==
        Boolean(order.expectedDeliveryDate) ||
      (refreshedOrder?.expectedDeliveryAt != null &&
        order.expectedDeliveryDate != null &&
        !dayjs(refreshedOrder.expectedDeliveryAt).isSame(
          dayjs(order.expectedDeliveryDate),
          "day",
        ));
    const basketWasReconciled =
      !refreshedOrder ||
      deliveryDateChanged ||
      basketItemsChanged(order.OrderItems ?? [], refreshedOrder.Items);
    if (basketWasReconciled) {
      setReconciliationError(true);
      return;
    }

    const selectionKey = selectionOverride ?? paymentMethods[supplierId] ?? "";
    if (!selectionKey) return;

    const isCreditAccount = selectionKey === "credit-account";
    const isSellarCredit = selectionKey === "sellar-credit";
    const isNewCard = selectionKey === "new-card";

    if (!business) return;

    if (isSellarCredit) {
      const latestCreditInfo = await sellarCreditBalanceQuery.refetch();
      const latestBalance = latestCreditInfo.data?.creditAmountAvailable;
      if (latestBalance != null && group.total > latestBalance) {
        setNonCardPlaceOrderError(INSUFFICIENT_SELLAR_CREDIT_MESSAGE);
        return;
      }
    }

    const checkoutOrderItems = mapBasketOrderToCheckoutOrderItems(
      order.OrderItems ?? [],
    );
    const validationResponse = await handleValidateCheckout({
      user: firebaseUser,
      business: business as BusinessGetResponseType,
      orderItemList: checkoutOrderItems,
      supplierId,
      storefrontLinkPassword: undefined,
    });
    if (validationResponse.outOfStockProducts.length > 0) {
      setValidationErrorSupplierId(supplierId);
      return;
    }
    if (
      validationResponse.pricingChangeProducts.length > 0 &&
      !acceptedPriceChange
    ) {
      await basketListQuery.refetch();
      setPendingPriceChange({
        supplierId,
        supplierName: group.info?.supplier.Business.name ?? "This supplier",
        currency: group.currency,
        products: validationResponse.pricingChangeProducts,
        stripe,
        stripeElements,
      });
      return;
    }
    if (validationResponse.pricingChangeProducts.length > 0) {
      const acceptedPrices = new Map(
        acceptedPriceChange?.products.map((product) => [
          product.id,
          product.newPrice,
        ]),
      );
      const pricesStillMatch = validationResponse.pricingChangeProducts.every(
        (product) => acceptedPrices.get(product.id) === product.newPrice,
      );
      if (!pricesStillMatch) {
        setPendingPriceChange({
          supplierId,
          supplierName: group.info?.supplier.Business.name ?? "This supplier",
          currency: group.currency,
          products: validationResponse.pricingChangeProducts,
          stripe,
          stripeElements,
        });
        return;
      }

      order = {
        ...order,
        OrderItems: order.OrderItems?.map((item) => ({
          ...item,
          price: acceptedPrices.get(item.ProductId) ?? item.price,
        })),
      };
      const pricing = calculateUnifiedCheckoutOrderPricing({
        orderItems: order.OrderItems ?? [],
        storefront: group.info?.storefront,
        vatPercentage: order.vatPercentage ?? 0,
      });
      group = {
        ...group,
        orders: [order, ...group.orders.slice(1)],
        subTotal: pricing.subtotal,
        discount: pricing.discount,
        vat: pricing.vat,
        total: pricing.total,
      };
    }
    const activeProductIds = new Set(
      validationResponse.activeProducts.map((product) => product.id),
    );
    const unavailableProducts = checkoutOrderItems
      .filter((item) => !activeProductIds.has(item.productId))
      .map((item) => ({
        id: item.productId,
        name: [item.title, item.description].filter(Boolean).join(" • "),
      }));
    if (unavailableProducts.length > 0) {
      setInactiveProducts(unavailableProducts);
      setValidationErrorSupplierId(supplierId);
      return;
    }

    const selectedPaymentMethod: SelectedPaymentMethod = (() => {
      if (isCreditAccount) {
        return {
          type: "credit-account",
          paymentLinkRequested: getPaymentLinkRequested(supplierId),
          invoiceMethod: invoiceMethods[supplierId] ?? "manual-invoice",
        };
      }
      if (isSellarCredit) {
        return { type: "sellar-credit" };
      }
      if (isNewCard) {
        return { type: "new-card-payment" };
      }
      const card = savedPaymentMethods.find(
        (c) => c.stripePaymentMethodId === selectionKey,
      );
      return {
        type: "existing-card-payment",
        paymentMethodId: selectionKey,
        brand: card?.brand,
        last4: card?.last4Digits,
      };
    })();

    const legacyPaymentType = mapToLegacyPaymentType({
      paymentType: selectedPaymentMethod.type as CheckoutPaymentType,
      invoiceMethod: selectedPaymentMethod.invoiceMethod ?? "manual-invoice",
    });

    const fulfillmentType = "sellarDistribution" as const;
    const isSellarDistribution = true;

    const orderItems = (order.OrderItems ?? []).map((item) => ({
      ProductId: item.ProductId,
      quantity: item.quantity,
      PromotionRewardItemId: item.PromotionRewardItemId ?? undefined,
    }));

    const orderData = {
      data: {
        storefrontLinkToken: undefined,
        checkoutSource: "unifiedCheckout" as const,
        initiator: "retailer" as const,
        paymentType: legacyPaymentType,
        InvoiceAddressId: business.InvoiceAddressId ?? 0,
        DeliveryAddressId: business.DeliveryAddressId ?? 0,
        vatPercentage: order.vatPercentage ?? 0,
        DeliveryAddress: sdBasket.deliveryAddress || business.DeliveryAddress,
        InvoiceAddress: sdBasket.deliveryAddress || business.InvoiceAddress,
        currency: group.currency,
        SupplierId: supplierId,
        purchaseOrderNumber: "",
        price: group.total,
        fulfillmentType,
        OrderItems: orderItems,
        deliveryInstructions: sdBasket.deliveryInstructions || "",
        orderComment: sdBasket.ordersComment || "",
        retailerContactSmsNumber: sdBasket.phoneNumber || null,
        mailingListPermission: false,
        hasNoDraughtDutyRelief: sdBasket.hasNoDraughtDutyRelief ?? false,
        paymentTermsRequest: undefined,
        ...(isSellarDistribution && {
          distributionContactId: sdBasket.contactId ?? undefined,
          RetailerSellarDistributionMeta: {
            timePreference: mapTimeSlotPreference(sdBasket.timeSlotPreference),
          },
        }),
        paymentLinkRequested: mapPaymentLinkRequested({
          paymentType: legacyPaymentType,
          paymentLinkRequested: selectedPaymentMethod.paymentLinkRequested,
        }),
      },
      sellarMetadata: {
        initiator: "retailer" as const,
        sessionId,
        priceTierLink: false,
        layout: "cards" as const,
        paymentIndication: selectedPaymentMethod.type as CheckoutPaymentType,
        temporaryCartId: sdBasket.uuid,
        unifiedCheckoutBasketUuid: sdBasket.uuid,
        unifiedCheckoutDraftOrderId: order.id,
        journeySource,
        supplierCount: supplierGroups.length,
        skuCount: supplierGroups.reduce(
          (count, supplierGroup) =>
            count +
            supplierGroup.orders.reduce(
              (orderCount, supplierOrder) =>
                orderCount + (supplierOrder.OrderItems?.length ?? 0),
              0,
            ),
          0,
        ),
      },
    };

    if (!deliveryInstructionsUpdateStartedRef.current) {
      deliveryInstructionsUpdateStartedRef.current = true;
      businessUpdateMutation.mutate({
        deliveryInstructions: sdBasket.deliveryInstructions || "",
      });
    }

    const processOrder = async () => {
      try {
        const isCardPayment =
          selectedPaymentMethod.type === "new-card-payment" ||
          selectedPaymentMethod.type === "existing-card-payment";
        const result = await (
          isCardPayment ? cardOrderCreateMutation : orderCreateMutation
        ).mutateAsync(orderData);
        return { data: { id: result.id }, error: null };
      } catch (error) {
        return {
          data: null,
          error: isHttpError(error) ? error : new Error("Something went wrong"),
        };
      }
    };

    if (isCreditAccount) {
      await placeOrder({
        type: "place-order-without-card",
        processOrder,
        onSuccess: async () => {
          if (sdOrders.length === 1) {
            navigate(
              UNIFIED_CHECKOUT_BASKET_SUCCESS.href({
                sdBasketUuid: sdBasket.uuid,
              }),
              { replace: true },
            );
          }
        },
      });
    } else if (isSellarCredit) {
      await placeOrder({
        type: "place-order-with-sellar-credit",
        processOrder,
        returnUrl: (orderId) =>
          `${window.location.origin}${UNIFIED_CHECKOUT_BASKET_PAY.href({
            sdBasketUuid: sdBasket.uuid,
          })}?paymentReturn=sellar-credit&supplierId=${supplierId}&orderId=${orderId}`,
      });
    } else if (stripe && stripeElements && isNewCard) {
      await payWithCard({
        stripeElements,
        stripe,
        processOrder,
        returnUrl: (orderId) =>
          `${window.location.origin}${UNIFIED_CHECKOUT_BASKET_PAY.href({
            sdBasketUuid: sdBasket.uuid,
          })}?paymentReturn=stripe&supplierId=${supplierId}&orderId=${orderId}`,
      });
    } else if (stripe && selectedPaymentMethod.paymentMethodId) {
      await payWithCard({
        stripePaymentMethodId: selectedPaymentMethod.paymentMethodId,
        stripe,
        processOrder,
        returnUrl: (orderId) =>
          `${window.location.origin}${UNIFIED_CHECKOUT_BASKET_PAY.href({
            sdBasketUuid: sdBasket.uuid,
          })}?paymentReturn=stripe&supplierId=${supplierId}&orderId=${orderId}`,
      });
    }
  };

  const handleBack = () =>
    navigate(
      UNIFIED_CHECKOUT_BASKET_DETAILS.href({ sdBasketUuid: sdBasket.uuid }),
    );

  const handlePayButton = (
    supplierId: number,
    stripe: Stripe | null,
    stripeElements: StripeElements | null,
  ) => {
    return handlePay(supplierId, stripe, stripeElements);
  };

  const activeGroup = supplierGroups.find(
    (g) => g.supplierId === activeSupplierId,
  );
  const dialogSupplierGroup = supplierGroups.find(
    (group) => group.supplierId === dialogSupplierId,
  );
  const activeSelection = activeSupplierId
    ? paymentMethods[activeSupplierId]
    : undefined;
  const isActiveCredit =
    activeSelection === "credit-account" || activeSelection === "sellar-credit";

  const isPageLoading =
    isLoading || (supplierGroups.length > 0 && supplierInfoMap.size === 0);

  if (isPageLoading) {
    return (
      <div className="flex min-h-screen flex-col bg-zinc-50">
        <CheckoutPageLoading
          title="Checkout"
          description="Loading checkout..."
          onClickLeftIcon={handleBack}
        />
      </div>
    );
  }

  const totalSupplierCount = new Set([
    ...supplierGroups.map((group) => group.supplierId),
    ...paidSupplierIds,
    ...(sdBasket.pendingSupplierIds ?? []),
    ...(sdBasket.failedSupplierIds ?? []),
  ]).size;

  const mainContent = (
    <div className="flex w-full max-w-242 flex-col gap-5 p-4 sm:gap-6">
      <div className="flex flex-col gap-2">
        <h2 className="text-lg font-semibold">Pay your suppliers</h2>
        <div className="flex flex-col items-start gap-1 sm:flex-row sm:items-center sm:gap-4">
          <p className="text-sm text-zinc-500">
            {
              new Set([
                ...paidSupplierIds,
                ...(sdBasket.pendingSupplierIds ?? []),
                ...(sdBasket.failedSupplierIds ?? []),
              ]).size
            }{" "}
            of {totalSupplierCount} supplier orders placed
          </p>
          <UnifiedCheckoutDeliveryDateSummary
            deliveryPreference={sdBasket.deliveryPreference}
            consolidatedDeliveryDate={sdBasket.deliveryDate}
            supplierDeliveryDates={sdOrders.map(
              (order) => order.expectedDeliveryDate,
            )}
            className="text-sm text-purple-600"
          />
        </div>
      </div>

      {/* The created-order card owns payment failures. Suppress the generic
          alert immediately so it cannot flash while the basket refreshes. */}
      <UnifiedCheckoutPayAlerts
        placeOrderError={failedPaymentOrderId ? "" : placeOrderError}
        isValidatingPaymentReturn={isValidatingPaymentReturn}
        paymentReturnError={paymentReturnError}
        reconciliationError={reconciliationError}
        reconciliationFetchError={reconciliationFetchError}
        showProductErrors={validationErrorSupplierId === activeSupplierId}
        inactiveProducts={inactiveProducts}
        outOfStockProducts={outOfStockProducts}
        supplierName={
          supplierInfoMap.get(activeSupplierId ?? 0)?.supplier.Business.name ??
          "The supplier"
        }
        onReviewUpdatedBaskets={() => navigate(DISCOVER_SEARCH)}
        onRemoveInactiveProduct={(productId) => {
          const item = sdOrders
            .flatMap((order) => order.OrderItems ?? [])
            .find((orderItem) => orderItem.ProductId === productId);
          if (item?.id == null) return;
          updateBasketItem.mutate(
            { basketUuid: sdBasket.uuid, itemId: item.id, quantity: 0 },
            {
              onSuccess: () => {
                setInactiveProducts((current) =>
                  current.filter((entry) => entry.id !== productId),
                );
              },
            },
          );
        }}
        onRemoveOutOfStockProduct={(productId) => {
          const item = sdOrders
            .flatMap((order) => order.OrderItems ?? [])
            .find((orderItem) => orderItem.ProductId === productId);
          if (item?.id == null) return;
          updateBasketItem.mutate(
            { basketUuid: sdBasket.uuid, itemId: item.id, quantity: 0 },
            { onSuccess: () => setValidationErrorSupplierId(null) },
          );
        }}
        onBackToMarket={() => navigate(DISCOVER_SEARCH)}
      />

      <div className="flex flex-col gap-4">
        {displayedSupplierIds.map((supplierId) => {
          const createdOrder = createdOrders.find(
            (order) => order.SupplierId === supplierId,
          );
          if (createdOrder) {
            return (
              <UnifiedCheckoutOrderPaymentStatus
                key={createdOrder.id}
                order={createdOrder}
                showOrderCard
              />
            );
          }

          const group = supplierGroups.find(
            (supplierGroup) => supplierGroup.supplierId === supplierId,
          );
          if (!group) return null;

          const selectionKey = paymentMethods[group.supplierId];
          const isPaid = paidSupplierIds.includes(group.supplierId);
          const isActive = activeSupplierId === group.supplierId;

          if (isPaid) {
            return null;
          }

          let selectedMethod: SelectedPaymentMethod = { type: "" };
          if (selectionKey === "credit-account") {
            selectedMethod = {
              type: "credit-account",
              paymentLinkRequested: getPaymentLinkRequested(group.supplierId),
            };
          } else if (selectionKey === "sellar-credit") {
            selectedMethod = {
              type: "sellar-credit",
            };
          } else if (selectionKey === "new-card") {
            selectedMethod = {
              type: "new-card-payment",
            };
          } else if (selectionKey) {
            const pm = savedPaymentMethods.find(
              (p) => p.stripePaymentMethodId === selectionKey,
            );
            selectedMethod = {
              type: "existing-card-payment",
              paymentMethodId: selectionKey,
              brand: pm?.brand,
              last4: pm?.last4Digits,
            };
          }

          return (
            <Fragment key={group.supplierId}>
              <SupplierPaymentCard
                supplierInfo={group.info}
                itemCount={group.itemCount}
                amount={group.total}
                currency={group.currency}
                vatPercentage={group.orders[0]?.vatPercentage ?? 0}
                isActive={isActive}
                selectedPaymentMethod={selectedMethod}
                onActivate={
                  !isActive && !isPlacingOrder && !isValidatingPaymentReturn
                    ? () => setActiveSupplierId(group.supplierId)
                    : undefined
                }
                availablePaymentTypes={isActive ? (availableOptions ?? []) : []}
                savedPaymentMethods={isActive ? savedPaymentMethods : []}
                sellarCreditVerificationRequired={
                  creditProvider === "two" &&
                  isCompanyVerified === false &&
                  sellarCreditStatus === "approved"
                }
                sellarCreditBalance={sellarCreditBalance}
                canUserViewSellarCreditBalance={canUserViewSellarCreditBalance}
                invoiceMethod={
                  invoiceMethods[group.supplierId] ?? "manual-invoice"
                }
                invoiceEmails={business?.invoiceEmails ?? []}
                onSelectPaymentMethod={(selection) => {
                  if (isPlacingOrder || isValidatingPaymentReturn) return;
                  setPaymentMethods((current) => ({
                    ...current,
                    [group.supplierId]:
                      selection.paymentType === "credit-account"
                        ? "credit-account"
                        : selection.paymentType === "sellar-credit"
                          ? "sellar-credit"
                          : selection.paymentType === "new-card-payment"
                            ? "new-card"
                            : (selection.paymentMethodId ?? ""),
                  }));
                  if (selection.invoiceMethod) {
                    setInvoiceMethods((current) => ({
                      ...current,
                      [group.supplierId]: selection.invoiceMethod!,
                    }));
                  }
                  if (selection.paymentLinkRequested !== undefined) {
                    setPaymentLinkRequests((current) => ({
                      ...current,
                      [group.supplierId]: selection.paymentLinkRequested!,
                    }));
                  }
                }}
                onOpenPaymentLinkSettings={() => {
                  if (isPlacingOrder || isValidatingPaymentReturn) return;
                  setActiveSupplierId(group.supplierId);
                  setDialogSupplierId(group.supplierId);
                }}
              />
              {isActive && activeGroup && (
                <PayButton
                  isActiveCredit={isActiveCredit}
                  activeSelection={activeSelection}
                  isPlacingOrder={isPlacingOrder || isValidatingPaymentReturn}
                  activeGroup={activeGroup}
                  onPay={handlePayButton}
                />
              )}
            </Fragment>
          );
        })}
      </div>

      {sdOrders.length === 0 && createdOrders.length > 0 && (
        <Button
          onClick={() =>
            navigate(
              UNIFIED_CHECKOUT_BASKET_SUCCESS.href({
                sdBasketUuid: sdBasket.uuid,
              }),
            )
          }
        >
          View order summary
        </Button>
      )}

      {activeSupplierId && supplierInfoMap.get(activeSupplierId) && (
        <UnifiedCheckoutPaymentLinkSettingsDialog
          isOpen={dialogSupplierId === activeSupplierId}
          onDismiss={() => setDialogSupplierId(null)}
          supplierName={
            supplierInfoMap.get(activeSupplierId)!.supplier.Business.name
          }
          stripeAccountId={
            supplierInfoMap.get(activeSupplierId)!.supplier.stripeAccountId
          }
          initialSettings={{
            invoiceMethod: invoiceMethods[activeSupplierId] ?? "manual-invoice",
            paymentLinkRequested: getPaymentLinkRequested(activeSupplierId),
          }}
          onConfirm={(selection) => {
            setPaymentLinkRequests((current) => ({
              ...current,
              [activeSupplierId]: selection.paymentLinkRequested ?? false,
            }));
            setInvoiceMethods((current) => ({
              ...current,
              [activeSupplierId]: selection.invoiceMethod ?? "manual-invoice",
            }));
          }}
          invoiceEmails={business?.invoiceEmails || []}
          onUpdateInvoiceEmails={async (emails) => {
            await businessUpdateMutation.mutateAsync({ invoiceEmails: emails });
          }}
          orderTotal={dialogSupplierGroup?.total ?? 0}
        />
      )}
    </div>
  );

  return (
    <div className="flex min-h-screen flex-col bg-white min-[1024px]:h-screen min-[1024px]:min-h-0">
      <CheckoutPageLayout
        title="Checkout"
        alerts={null}
        aside={
          <CheckoutOrderSummary
            orderItemList={allCheckoutItems}
            outOfStockProductIds={[]}
            productIdsWithPricingChange={[]}
            activeProductIdList={[]}
            orderSummary={totalOrderSummary}
            promotions={[]}
            showSupplierName
          />
        }
        onClickLeftIcon={handleBack}
        className="min-h-auto"
      >
        <div className="flex justify-center">
          {activeGroup ? (
            <AlwaysStripeProvider
              key={`${activeGroup.supplierId}:${activeGroup.info?.supplier.stripeAccountId ?? ""}:${activeGroup.currency}`}
              stripeAccountId={
                activeGroup.info?.supplier.stripeAccountId || undefined
              }
              amount={activeGroup.total}
              currency={activeGroup.currency}
            >
              {mainContent}
            </AlwaysStripeProvider>
          ) : (
            mainContent
          )}
        </div>
      </CheckoutPageLayout>
      <UnifiedCheckoutPriceChangeDialog
        priceChange={pendingPriceChange}
        onDismiss={() => setPendingPriceChange(null)}
        onBackToMarket={() => navigate(DISCOVER_SEARCH)}
        onAccept={() => {
          if (!pendingPriceChange) return;
          setPendingPriceChange(null);
          handlePay(
            pendingPriceChange.supplierId,
            pendingPriceChange.stripe,
            pendingPriceChange.stripeElements,
            undefined,
            pendingPriceChange,
          );
        }}
      />
    </div>
  );
};
